import React from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import * as Phosphor from "@phosphor-icons/react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const ICON_OPTIONS = [
  "SwimmingPool", "Umbrella", "MartiniGlass", "Fire", "WifiHigh",
  "Television", "ForkKnife", "Car", "Mountains", "Sun", "Moon",
  "Sparkle", "Coffee", "Bed", "Wind", "Bathtub", "Music",
];

export default function AdminAmenities() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState({ name: "", icon: "Sparkle", description: "", order: 0 });
  const [editingId, setEditingId] = React.useState(null);

  const load = () => api.get("/amenities").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/amenities/${editingId}`, form);
        toast.success("Updated");
      } else {
        await api.post("/amenities", form);
        toast.success("Added");
      }
      setForm({ name: "", icon: "Sparkle", description: "", order: 0 });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const edit = (a) => {
    setEditingId(a.id);
    setForm({ name: a.name, icon: a.icon, description: a.description, order: a.order });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this amenity?")) return;
    await api.delete(`/amenities/${id}`);
    toast.success("Removed");
    load();
  };

  const PreviewIcon = Phosphor[form.icon] || Phosphor.Sparkle;

  return (
    <div data-testid="admin-amenities-page">
      <div className="mb-8">
        <p className="overline mb-2">Curated Comforts</p>
        <h1 className="font-serif-display text-4xl">Amenities</h1>
      </div>

      <form
        onSubmit={save}
        className="border border-[var(--border)] rounded-2xl p-6 mb-10 bg-[#13151D]"
        data-testid="amenity-form"
      >
        <h3 className="font-serif-display text-xl mb-4">
          {editingId ? "Edit Amenity" : "Add Amenity"}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Name</Label>
            <Input
              value={form.name}
              required
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="amenity-name-input"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Icon</Label>
            <div className="flex gap-2 mt-2 items-center">
              <select
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="flex-1 bg-[#0A0B10] border border-[var(--border)] rounded-md py-2 px-3 text-sm"
                data-testid="amenity-icon-select"
              >
                {ICON_OPTIONS.map((i) => (<option key={i} value={i}>{i}</option>))}
              </select>
              <span className="w-10 h-10 rounded border border-[var(--border)] grid place-items-center text-[#C5A059]">
                <PreviewIcon size={20} weight="thin" />
              </span>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              rows={2}
              data-testid="amenity-description-input"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Order</Label>
            <Input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="amenity-order-input"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button type="submit" className="gold-btn rounded-full px-6" data-testid="amenity-save-button">
            <Plus size={14} className="mr-2" />
            {editingId ? "Update" : "Add"}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditingId(null);
                setForm({ name: "", icon: "Sparkle", description: "", order: 0 });
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((a) => {
          const Icon = Phosphor[a.icon] || Phosphor.Sparkle;
          return (
            <div
              key={a.id}
              className="border border-[var(--border)] rounded-xl p-5 bg-[#13151D] flex items-start gap-4"
              data-testid={`amenity-item-${a.id}`}
            >
              <div className="text-[#C5A059] mt-1">
                <Icon size={24} weight="thin" />
              </div>
              <div className="flex-1">
                <div className="font-serif-display text-lg">{a.name}</div>
                <div className="text-xs text-[#A1A4B5]">{a.description}</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="bg-transparent border-[var(--border)]" onClick={() => edit(a)} data-testid={`edit-amenity-${a.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)} data-testid={`delete-amenity-${a.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
